import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarDays, Video, MapPin, Megaphone, UserPlus, Copy, Users, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

import RichTextEditor from "@/components/RichTextEditor";
const TYPE = { center_session: "Center session", group_event: "Group event", broadcast: "Broadcast", class: "Group call", event: "Event" };

export const ScheduleList = ({ items, onRegister, testid = "program-schedule" }) => (
  <ul className="divide-y divide-[#F1EDE6]" data-testid={testid}>
    {items.length === 0 && <li className="py-4 text-sm text-[#8FA89B]">Nothing scheduled yet.</li>}
    {items.map((s) => (
      <li key={`${s.type}-${s.id}`} className="py-3 flex flex-wrap items-center gap-3" data-testid={`${testid}-${s.id}`}>
        <span className="w-28 text-sm text-[#1A242B]">{s.date || "TBD"} <span className="text-[#8FA89B]">{s.start_time}</span></span>
        <span className="flex-1 min-w-[160px]">
          <span className="block text-[#1A242B]">{s.title}</span>
          <span className="text-xs text-[#8FA89B] inline-flex items-center gap-1">
            {s.is_broadcast ? <Video size={12} /> : s.type === "center_session" ? <MapPin size={12} /> : <CalendarDays size={12} />}
            {s.is_broadcast ? "Broadcast" : TYPE[s.type] || "Session"}{s.center ? ` · ${s.center}` : ""}{s.room ? ` · ${s.room}` : ""}{s.member ? ` · ${s.member}` : ""}{s.group ? ` · ${s.group}` : ""}
          </span>
        </span>
        {s.is_broadcast ? <Link to={`/events/${s.id}/watch`} className="eyw-btn-ghost !py-1.5 text-sm" data-testid={`watch-${s.id}`}>Watch</Link>
          : ["class", "event", "broadcast"].includes(s.type) && onRegister ? (s.registered
            ? <span className="text-xs text-[#3F6B55]" data-testid={`registered-${s.id}`}>You're registered</span>
            : <button onClick={() => onRegister(s.id)} className="eyw-btn !py-1.5 text-sm" data-testid={`register-${s.id}`}>Register</button>) : null}
      </li>
    ))}
  </ul>
);

const Bar = ({ pct }) => <div className="h-2 rounded-full bg-[#E5E0D8] overflow-hidden"><div className="h-full bg-[#5C8A72]" style={{ width: `${pct}%` }} /></div>;

export const ProgressLine = ({ p }) => (
  <div className="text-xs text-[#4A5D6B] space-y-1">
    <Bar pct={p.pct} />
    <p>{p.pct}% · sessions {p.sessions_attended}/{p.sessions_total} attended ({p.sessions_remaining} left) · calls {p.calls_attended}/{p.calls_total} · broadcasts {p.broadcasts_watched}/{p.broadcasts_total}</p>
  </div>
);

const HostTools = ({ group, data }) => {
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const { data: prog } = useQuery({ queryKey: ["program-progress", group.id], queryFn: () => api.get(`/programs/groups/${group.id}/progress`).then((r) => r.data) });
  const run = async (fn, ok) => { try { await fn(); toast.success(ok); qc.invalidateQueries({ queryKey: ["program", group.id] }); qc.invalidateQueries({ queryKey: ["program-progress", group.id] }); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (
    <div className="space-y-6" data-testid="program-host-tools">
      <div className="flex flex-wrap gap-4 text-sm">
        <span className="eyw-card px-4 py-2" data-testid="program-member-count"><Users size={14} className="inline mr-1" />{data.member_count}{data.capacity ? ` / ${data.capacity}` : ""} members</span>
        <span className="eyw-card px-4 py-2" data-testid="program-waitlist-count">{data.waitlist_count} on waitlist</span>
        <button onClick={() => run(() => api.post(`/programs/groups/${group.id}/template`), "Saved as a template — open new groups from your dashboard.")} className="eyw-btn-ghost !py-2 text-sm" data-testid="program-save-template"><Copy size={14} /> Save as template</button>
      </div>
      <div className="eyw-card p-5">
        <p className="eyw-label mb-2 flex items-center gap-1"><Megaphone size={13} /> Announcement</p>
        <RichTextEditor value={note} onChange={setNote} testid="program-announce-input" placeholder="Tell everyone in the group…" minHeight={90} enableBlocks={false} media={false} />
        <button disabled={!note.replace(/<[^>]+>|&nbsp;/g, "").trim()} onClick={() => run(() => api.post(`/programs/groups/${group.id}/announce`, { body: note }).then(() => setNote("")), "Announcement posted and members notified.")} className="eyw-btn mt-2 !py-2 text-sm" data-testid="program-announce-btn">Post & notify</button>
      </div>
      <div className="eyw-card p-5">
        <p className="eyw-label mb-2 flex items-center gap-1"><UserPlus size={13} /> Add a client who paid in person</p>
        <div className="flex gap-2">
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="client@email.com" className="eyw-input flex-1 min-w-0" data-testid="program-add-email" />
          <button disabled={!email.trim()} onClick={() => run(() => api.post(`/programs/groups/${group.id}/members`, { email }).then(() => setEmail("")), "Client added and welcomed.")} className="eyw-btn !py-2 text-sm" data-testid="program-add-btn">Add</button>
        </div>
      </div>
      <div className="eyw-card p-5">
        <p className="eyw-label mb-3">Client progress</p>
        {!prog ? <Loader2 className="animate-spin" /> : prog.members.length === 0 ? <p className="text-sm text-[#8FA89B]">No members yet.</p> : (
          <ul className="space-y-4" data-testid="program-progress-list">
            {prog.members.map((m) => (
              <li key={m.user_id} data-testid={`program-progress-${m.user_id}`}>
                <p className="text-sm text-[#1A242B]">{m.name || m.email} {m.source === "manual" && <span className="text-xs text-[#8FA89B]">· added manually</span>}</p>
                <ProgressLine p={m} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default function ProgramPanel({ group }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["program", group.id], queryFn: () => api.get(`/programs/groups/${group.id}`).then((r) => r.data) });
  if (isLoading || !data) return <Loader2 className="animate-spin text-[#144F75]" />;
  const register = async (eid) => {
    try { await api.post(`/programs/events/${eid}/register`); toast.success("You're registered."); qc.invalidateQueries({ queryKey: ["program", group.id] }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-6" data-testid="program-panel">
      <div className="eyw-card p-5">
        <p className="text-sm text-[#4A5D6B]">Program: <b className="text-[#1A242B]">{data.package.name}</b> · Hosted by {data.hosts.join(", ") || "your enhancer"}</p>
        {data.my_progress && <div className="mt-3" data-testid="program-my-progress"><ProgressLine p={data.my_progress} /></div>}
        {!data.is_host && <Link to="/dashboard" className="text-sm text-[#144F75] underline mt-2 inline-block" data-testid="program-book-link">Book your sessions from My packages</Link>}
      </div>
      <div className="eyw-card p-5">
        <p className="eyw-label mb-2">Shared schedule</p>
        <ScheduleList items={data.schedule} onRegister={data.is_host ? null : register} />
      </div>
      {data.is_host && <HostTools group={group} data={data} />}
    </div>
  );
}
